import axios from 'axios'
import isEmpty from 'lodash-es/isEmpty.js'
import range from 'lodash-es/range.js'
import { batchActions } from 'redux-batched-actions'

import { getTenantApi, getUserApi } from '@kistler/skybase-oauth/rest'
import { createAction } from '@kistler/skybase-ui/skybase-core/base/create-action'
import { getActionName } from '@kistler/skybase-ui/skybase-core/utils/get-action-name'

import { showErrorToast, showSuccessToast } from '@/common/services/show-toast'
import { MAX_CALIBRATIONS_TO_LOAD_PER_REQUEST } from '@/fleet-configuration/data-fleet/calibration/calibration-constants'
import {
  getCalibrationItems,
  getCalibrationSettingSlice,
  getCalibrationUserData,
  getCalibrationWarningDays,
} from '@/fleet-configuration/data-fleet/calibration/calibration-selectors'
import {
  getCalibrationSetting as fetchCalibrationSetting,
  putCalibrationSetting as putCalibrationSettingApi,
} from '@/fleet-configuration/components/calibration/calibration-intervals-modal/calibration-intervals-modal-api'
import { equipmentApi } from '@/fleet-configuration/equipment'
import { getStudioAPIHost } from '@/utils/url'
import { messages as t } from './calibration-actions-i18n.js'

export const SET_CALIBRATION_USER_DATA = getActionName('SET_CALIBRATION_USER_DATA')
export const setCalibrationUserData = userData => createAction(SET_CALIBRATION_USER_DATA, userData)

export const SET_CALIBRATION_FORM_DATA = getActionName('SET_CALIBRATION_FORM_DATA')
export const setCalibrationFormData = formData => createAction(SET_CALIBRATION_FORM_DATA, formData)

export const SET_CALIBRATION_FORM_DATA_ERRORS = getActionName('SET_CALIBRATION_FORM_DATA_ERRORS')
export const setCalibrationFormDataErrors = (field, alerts) =>
  createAction(SET_CALIBRATION_FORM_DATA_ERRORS, { [field]: alerts })

export const SET_CALIBRATION_ITEM = getActionName('SET_CALIBRATION_ITEM')
export const setCalibrationItem = (typeNumber, serialNumber, calibrationItem) =>
  createAction(SET_CALIBRATION_ITEM, {
    id: JSON.stringify({ typeNumber, serialNumber }),
    typeNumber,
    serialNumber,
    calibrationItem,
  })

export const SET_CALIBRATION_SETTING = getActionName('SET_CALIBRATION_SETTING')
export const setCalibrationSetting = setting => createAction(SET_CALIBRATION_SETTING, setting)

// Module-scoped promise prevents duplicate concurrent requests when multiple components
// mount in the same page render and each kicks off the load.
let inflightSettingLoad = null

export const loadCalibrationSettingIfEmpty = () => async (dispatch, getState) => {
  const slice = getCalibrationSettingSlice(getState())
  if (slice.data) {
    return slice.data
  }
  if (inflightSettingLoad) {
    return inflightSettingLoad
  }
  inflightSettingLoad = (async () => {
    try {
      const data = await fetchCalibrationSetting()
      dispatch(setCalibrationSetting(data))
      return data
    } finally {
      inflightSettingLoad = null
    }
  })()
  return inflightSettingLoad
}

export const saveCalibrationSetting = payload => async dispatch => {
  await putCalibrationSettingApi(payload)
  dispatch(setCalibrationSetting(payload))
}

export const tryLoadCalibrationUserData = (tenantId, userId) => async (dispatch, getState) => {
  let userData = getCalibrationUserData(getState())
  if (!userData || isEmpty(userData)) {
    const [{ name: tenantName }, { email: userEmail, name: userName }] = await Promise.all([
      getTenantApi(tenantId).catch(error => {
        // 403 is normal workflow for people that don't have rights to see tenant. Consider it empty response
        if (error?.status === 403) {
          return {}
        }
        showErrorToast(t.anErrorOccurredWhileFetchingCompanyInformation, t.error)
        return {}
      }),
      getUserApi(userId).catch(() => {
        showErrorToast(t.anErrorOccurredWhileFetchingTheUsers, t.error)
        return {}
      }),
    ])
    userData = {
      email: userEmail ?? '',
      name: userName ?? '',
      company: tenantName ?? '',
    }
    dispatch(setCalibrationUserData(userData))
  }
  return userData
}

export const sendOrderCalibrationMail = async (subject, email, name, company, typeNumber, serialNumber, note) => {
  try {
    await axios.post(
      `${getStudioAPIHost()}/api/calibrations/email`,
      {
        subject,
        userEmail: email.trim(),
        userName: name.trim(),
        userCompany: company.trim(),
        typeNumber,
        serialNumber,
        note,
      },
      { customErrorHandler: () => {} },
    )
    showSuccessToast(t.yourOrderCalibrationWasSuccessfullySent)
    return true
  } catch (e) {
    if (e.response?.status === 403) {
      showErrorToast(t.pleaseContactAdmin, t.yourCalibrationOrderIsForbidden)
    } else {
      showErrorToast(t.pleaseTryAgainLater, t.yourCalibrationOrderCouldNotBeSent)
    }
    return false
  }
}

// Extend the calibrations list with some helper fields. `warningDays` is the per-tenant
// threshold from the calibration setting (passed in from the thunk so this function stays pure).
const addCalibrationHelperFields = (calibration, warningDays) => {
  let notifications = 0
  if (!calibration.calibrationDate) {
    return calibration
  }
  const calibrationExpires = new Date(calibration.calibrationDate)
  calibrationExpires.setDate(calibrationExpires.getDate() + calibration.calibrationIntervalDays)
  const today = new Date()
  const warningThreshold = new Date()
  warningThreshold.setDate(warningThreshold.getDate() + warningDays)
  const isExpired = today.getTime() > calibrationExpires.getTime()
  const isExpiringSoon = !isExpired && warningThreshold.getTime() > calibrationExpires.getTime()
  const isExpiring = isExpired || isExpiringSoon

  if (isExpiring) {
    notifications += 1
  }

  return { ...calibration, isExpired, isExpiringSoon, isExpiring, notifications }
}

export const loadMissingCalibrations =
  (typeAndSerialNumberArr = []) =>
  async (dispatch, getState) => {
    // Ensure the per-tenant warning-days setting is hydrated before we stamp calibration items
    // with isExpired/isExpiringSoon flags, so they're computed against the right threshold.
    await dispatch(loadCalibrationSettingIfEmpty())
    const loadedCalibrations = getCalibrationItems(getState())
    const itemsToLoad = typeAndSerialNumberArr
      .map(itm => ({
        ...itm,
        typeNumber: itm.typeNumber?.toUpperCase?.(),
        serialNumber: itm.serialNumber?.toUpperCase?.(),
      }))
      .filter(({ typeNumber, serialNumber }) => {
        if (!typeNumber || !serialNumber) {
          return false
        }

        const calibrationKey = JSON.stringify({ typeNumber, serialNumber })
        const calibration = loadedCalibrations[calibrationKey]
        return !calibration || calibration.hasRequestError
      })
    if (itemsToLoad.length) {
      // first mark all items as being loaded
      dispatch(
        batchActions(
          itemsToLoad.map(item =>
            setCalibrationItem(item.typeNumber, item.serialNumber, {
              typeNumber: item.typeNumber,
              serialNumber: item.serialNumber,
              isLoading: true,
            }),
          ),
        ),
      )

      const numberOfCalibrationRequests = itemsToLoad.length / MAX_CALIBRATIONS_TO_LOAD_PER_REQUEST
      // load calibrations per batch size - each batch sequentially (wait for previous item to finish)
      //  The reason for this is, that /api/calibrations can't handle too much stress, so this code gives a bit of breathing room to it - see DHUB-5211
      range(0, numberOfCalibrationRequests).reduce(async (previousPromise, currentDataIndex) => {
        const itemsBatch = itemsToLoad
          .slice(
            currentDataIndex * MAX_CALIBRATIONS_TO_LOAD_PER_REQUEST,
            (currentDataIndex + 1) * MAX_CALIBRATIONS_TO_LOAD_PER_REQUEST,
          )
          .map(item => ({
            typeNumber: item.typeNumber,
            serialNumber: item.serialNumber,
            familyType: item.family === '' ? null : item.family,
          }))
        // by using "previousPromise.then" we are forcing waiting for last request to finish, before new one starts.
        //  a side effect from this one is, that "previousPromise" must resolve, otherwise one failed request would skip all following requests.
        //  To prevent this, we need to have ".catch" part (that can never throw).
        const { data } = await previousPromise.then(() =>
          axios
            .post(`${getStudioAPIHost()}${equipmentApi.CALIBRATIONS}`, itemsBatch)
            .catch(() => ({ data: itemsBatch.map(item => ({ ...item, hasRequestError: true })) })),
        )
        const warningDays = getCalibrationWarningDays(getState())
        dispatch(
          batchActions(
            data.map(item =>
              setCalibrationItem(
                itemsBatch.find(
                  itemToLoad =>
                    itemToLoad.typeNumber.startsWith(item.baseTypeNumber) &&
                    itemToLoad.serialNumber === item.serialNumber,
                )?.typeNumber || item.baseTypeNumber,
                item.serialNumber,
                addCalibrationHelperFields(item, warningDays),
              ),
            ),
          ),
        )
        // mark all remaining calibrations from this batch as already loaded
        //  This happens only if baseTypeNumber covers more then 1 equipment item
        if (data.length < itemsBatch.length) {
          const allCalibrations = getCalibrationItems(getState())
          const noLongerLoadingCalibrationItems = []
          itemsBatch.forEach(batchItem => {
            const calibrationKey = JSON.stringify({
              typeNumber: batchItem.typeNumber,
              serialNumber: batchItem.serialNumber,
            })
            if (allCalibrations[calibrationKey].calibrationItem.isLoading) {
              noLongerLoadingCalibrationItems.push(
                setCalibrationItem(batchItem.typeNumber, batchItem.serialNumber, {
                  ...allCalibrations[calibrationKey].calibrationItem,
                  isLoading: false,
                }),
              )
            }
          })
          dispatch(batchActions(noLongerLoadingCalibrationItems))
        }
      }, Promise.resolve())
    }
  }
